import { useEffect } from "react";

const DEFAULT_TITLE = "Makchester Tours & Travel | International Travel Company";
const DEFAULT_DESC = "Makchester Tours & Travel is an international travel company registered in the United Arab Emirates, Zimbabwe and South Africa, offering flights, visas, hotels, holiday packages and travel solutions worldwide.";
const SITE_URL = "https://makchester.com";

function setMeta(selector, attr, value) {
  const el = document.querySelector(selector);
  if (el) el.setAttribute(attr, value);
}

function setJsonLd(id, data) {
  let el = document.getElementById(id);
  if (!data) { if (el) el.remove(); return; }
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

export function usePageMeta(title, description) {
  useEffect(() => {
    const fullTitle = title ? `${title} | Makchester Tours & Travel` : DEFAULT_TITLE;
    const desc = description || DEFAULT_DESC;
    const url = `${SITE_URL}${window.location.pathname}`;
    document.title = fullTitle;
    setMeta('meta[name="description"]', "content", desc);
    let robots = document.querySelector('meta[name="robots"]:not([data-noindex])');
    if (!robots) {
      robots = document.createElement("meta");
      robots.name = "robots";
      document.head.appendChild(robots);
    }
    robots.content = "index, follow";
    setMeta('meta[property="og:title"]', "content", fullTitle);
    setMeta('meta[property="og:description"]', "content", desc);
    setMeta('meta[property="og:url"]', "content", url);
    setMeta('meta[name="twitter:title"]', "content", fullTitle);
    setMeta('meta[name="twitter:description"]', "content", desc);
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", url);
    const crumbs = [{ "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` }];
    if (title && window.location.pathname !== "/") {
      crumbs.push({ "@type": "ListItem", position: 2, name: title, item: url });
    }
    setJsonLd("ld-breadcrumb", { "@context": "https://schema.org", "@type": "BreadcrumbList", itemListElement: crumbs });
    return () => { document.title = DEFAULT_TITLE; };
  }, [title, description]);
}

export function useJsonLd(id, data) {
  useEffect(() => {
    setJsonLd(id, data);
    return () => setJsonLd(id, null);
  }, [id, data]);
}

export function useNoIndex() {
  useEffect(() => {
    const el = document.createElement("meta");
    el.name = "robots";
    el.content = "noindex, nofollow";
    el.setAttribute("data-noindex", "true");
    document.head.appendChild(el);
    return () => el.remove();
  }, []);
}

const LOGO_URL = `${SITE_URL}/logo-color.png`;
const BUSINESS_DESC = "Makchester Tours & Travel is an international travel company registered and operating in the United Arab Emirates, Zimbabwe and South Africa, providing visa assistance, flights, hotels, holiday packages, tours, airport transfers and travel services worldwide.";

const OFFICES_LD = [
  {
    "@type": ["TravelAgency", "Place"], "@id": `${SITE_URL}/#office-ae`,
    name: "Makchester Tours & Travel — Sharjah Office",
    image: LOGO_URL, telephone: "+971559209503", priceRange: "$$",
    address: { "@type": "PostalAddress", streetAddress: "Sharjah Media City, Al Messaned, Al Bataeh", addressLocality: "Sharjah", addressCountry: "AE" },
  },
  {
    "@type": ["TravelAgency", "Place"], "@id": `${SITE_URL}/#office-zw`,
    name: "Makchester Tours & Travel — Harare Office",
    image: LOGO_URL, telephone: "+971559209503", priceRange: "$$",
    address: { "@type": "PostalAddress", streetAddress: "Office 207, Gelfand House, Corner 1st Street and Speke Avenue", addressLocality: "Harare", addressCountry: "ZW" },
  },
  {
    "@type": ["TravelAgency", "Place"], "@id": `${SITE_URL}/#office-za`,
    name: "Makchester Tours & Travel — Johannesburg Office",
    image: LOGO_URL, telephone: "+971559209503", priceRange: "$$",
    address: { "@type": "PostalAddress", streetAddress: "Office 22, Startek Building Block B, 5 Eglin Road, Sunninghill, Sandton", addressLocality: "Johannesburg", addressCountry: "ZA" },
  },
];

const SERVICES_LD = [
  "UAE and Dubai visa assistance", "Flight bookings", "Hotel bookings",
  "Holiday packages", "Tours and activities", "Airport transfers", "Worldwide travel services",
].map((name) => ({ "@type": "Offer", itemOffered: { "@type": "Service", name, provider: { "@id": `${SITE_URL}/#travelagency` } } }));

const BUSINESS_SCHEMA = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Organization",
      "@id": `${SITE_URL}/#organization`,
      name: "Makchester Tours & Travel",
      alternateName: "Makchester",
      slogan: "Your Gateway To The World",
      url: `${SITE_URL}/`,
      logo: { "@type": "ImageObject", "@id": `${SITE_URL}/#logo`, url: LOGO_URL, contentUrl: LOGO_URL, caption: "Makchester Tours & Travel" },
      image: LOGO_URL,
      email: "explore@makchester.com",
      telephone: "+971559209503",
      description: BUSINESS_DESC,
      address: { "@type": "PostalAddress", streetAddress: "Sharjah Media City, Al Messaned, Al Bataeh", addressLocality: "Sharjah", addressCountry: "AE" },
      sameAs: [
        "https://www.instagram.com/makchestertours/",
        "https://x.com/MakchesterTours",
        "https://www.tiktok.com/@makchester_toursntravel",
        "https://www.facebook.com/MakchesterTours",
      ],
      contactPoint: [{ "@type": "ContactPoint", telephone: "+971559209503", email: "explore@makchester.com", contactType: "customer service", availableLanguage: ["English"] }],
    },
    {
      "@type": "TravelAgency",
      "@id": `${SITE_URL}/#travelagency`,
      name: "Makchester Tours & Travel",
      alternateName: "Makchester",
      slogan: "Your Gateway To The World",
      parentOrganization: { "@id": `${SITE_URL}/#organization` },
      url: `${SITE_URL}/`,
      logo: { "@id": `${SITE_URL}/#logo` },
      image: LOGO_URL,
      email: "explore@makchester.com",
      telephone: "+971559209503",
      description: BUSINESS_DESC,
      currenciesAccepted: "AED, USD",
      paymentAccepted: "Visa, Mastercard, Apple Pay, Google Pay",
      priceRange: "$$",
      areaServed: [
        { "@type": "Country", name: "United Arab Emirates" },
        { "@type": "Country", name: "Zimbabwe" },
        { "@type": "Country", name: "South Africa" },
        "Worldwide",
      ],
      address: OFFICES_LD[0].address,
      location: OFFICES_LD.map((o) => ({ "@id": o["@id"] })),
      hasOfferCatalog: { "@type": "OfferCatalog", name: "Travel Services", itemListElement: SERVICES_LD },
    },
    {
      "@type": "WebSite",
      "@id": `${SITE_URL}/#website`,
      url: `${SITE_URL}/`,
      name: "Makchester Tours & Travel",
      alternateName: "Makchester",
      description: BUSINESS_DESC,
      publisher: { "@id": `${SITE_URL}/#organization` },
    },
    ...OFFICES_LD,
  ],
};

export function useBusinessSchema(enabled) {
  useEffect(() => {
    setJsonLd("ld-business", enabled ? BUSINESS_SCHEMA : null);
    return () => setJsonLd("ld-business", null);
  }, [enabled]);
}
